import { DEFAULT_EXAM_CATEGORIES } from "@/lib/exam-categories/defaults"
import type { ExamCategory } from "@/lib/exam-categories/types"
import {
  findCategory,
  getCategoryAncestors,
  getCategoryPathLabel,
  getRootCategoryId,
} from "@/lib/exam-categories/tree"

/** Map bank topics to domain category group ids (level 1). */
const TOPIC_TO_CONTENT: Record<string, string> = {
  Cardiology: "cardiology",
  "Cardiovascular system": "cardiology",
  "Cardiovascular Conditions": "cardiology",
  Cardiopulmonary: "cardiology",
  Neurology: "neurology",
  Physiology: "pulmonology",
  Respiratory: "pulmonology",
  Renal: "laboratory-interpretation",
  Endocrinology: "endocrinology",
  Musculoskeletal: "diagnostic-reasoning",
  Pharmacology: "pharmacology",
  "Pharmacology and therapeutics": "pharmacology",
  "Pharmacology and Therapeutics": "pharmacology",
  Pathology: "laboratory-interpretation",
  Biochemistry: "laboratory-interpretation",
  Chemistry: "laboratory-interpretation",
  "Cell Biology": "laboratory-interpretation",
  Enzymology: "laboratory-interpretation",
  Anatomy: "diagnostic-reasoning",
  Immunology: "infectious-disease",
  Microbiology: "infectious-disease",
  Clinical: "diagnostic-reasoning",
  "Clinical reasoning": "diagnostic-reasoning",
  "Diagnostic Reasoning": "diagnostic-reasoning",
  Documentation: "professionalism-ethics",
  Geriatrics: "patient-communication",
  General: "diagnostic-reasoning",
  "Emergency Recognition": "emergency-recognition",
  "Patient Communication": "patient-communication",
  "Patient Safety": "patient-safety",
  "Professionalism and Ethics": "professionalism-ethics",
  Pediatrics: "pediatrics",
  "Pediatric Conditions": "pediatrics",
  "Women's Health": "womens-health",
  "Infectious Disease": "infectious-disease",
  "Infectious Disease and Microbiology": "infectious-disease",
  "Laboratory Interpretation": "laboratory-interpretation",
  "Pulmonary Conditions": "pulmonology",
  "Neurologic Conditions": "neurology",
  "Endocrine Conditions": "endocrinology",
  "Gait & Posture Analysis": "diagnostic-reasoning",
  "Manual Therapy Techniques": "patient-safety",
  "Exercise Physiology": "pulmonology",
  "HIPAA & Confidentiality": "professionalism-ethics",
  "Case Studies": "diagnostic-reasoning",
}

/** Map bank topics to leaf category ids where a finer tag is known. */
const TOPIC_TO_LEAF: Record<string, string> = {
  Immunology: "infectious-disease-identify-the-organism",
  Microbiology: "understand-transmission",
  Cardiology: "cardiology-recognize-cardiovascular-conditions",
  Pharmacology: "pharmacology-select-the-appropriate-medication",
  "Diagnostic Reasoning": "diagnostic-reasoning-identify-the-key-clinical-problem",
  "Emergency Recognition": "emergency-recognition-identify-unstable-patient",
  "Patient Communication": "patient-communication-counsel-patient",
}

/** Map Bloom level on a question to the prototype cognitive-axis id. */
const BLOOM_TO_TASK: Record<string, string> = {
  Remembering: "remembering", Remember: "remembering", Recall: "remembering",
  Understanding: "understanding", Understand: "understanding",
  Applying: "applying", Apply: "applying",
  Analyzing: "analyzing", Analyze: "analyzing",
  Evaluating: "evaluating", Evaluate: "evaluating",
  Creating: "creating", Create: "creating",
}

export function contentIdForTopic(topic: string | null | undefined): string | null {
  if (topic == null) return null
  const trimmed = topic.trim()
  if (!trimmed) return null
  return TOPIC_TO_CONTENT[trimmed] ?? null
}

/** Exam category domain id for a bank row, derived from its topic folder. */
export function categoryIdForQuestion(
  row: { topic?: string | null }
): string | null {
  return contentIdForTopic(row.topic)
}

/** Composer Classification seed: leaf when known, otherwise the domain. */
export function categoryIdsFromTopic(topic?: string | null): string[] {
  const id = leafIdForTopic(topic) ?? contentIdForTopic(topic)
  return id ? [id] : []
}

export function categoryDetailsForQuestion(row: {
  topic?: string | null
  categoryIds?: readonly string[] | null
  categoryUnassigned?: boolean
}): {
  id: string
  path: string
  domain: string
  skill: string | null
  description: string | null
} | null {
  const explicit = row.categoryIds?.find((categoryId) => findCategory(DEFAULT_EXAM_CATEGORIES, categoryId))
  if (!explicit && row.categoryUnassigned) return null
  const id = explicit ?? leafIdForTopic(row.topic) ?? contentIdForTopic(row.topic)
  if (!id) return null
  const node = findCategory(DEFAULT_EXAM_CATEGORIES, id)
  const rootId = getRootCategoryId(DEFAULT_EXAM_CATEGORIES, id)
  const root = rootId
    ? findCategory(DEFAULT_EXAM_CATEGORIES, rootId)
    : undefined
  return {
    id,
    path: getCategoryPathLabel(DEFAULT_EXAM_CATEGORIES, id) || id,
    domain: root?.name ?? node?.name ?? id,
    skill: node && node.parentId ? node.name : null,
    description: root?.description ?? node?.description ?? null,
  }
}

/**
 * Every category a question carries, as names. The one reading all surfaces
 * share: the bank column, board and list cards, the sheet, the detail pane.
 * Explicit ids win; otherwise the topic maps onto the tree. Empty when neither
 * is known or the question is marked unassigned. Pass the live tree so a
 * category the program added resolves too.
 */
export function categoryNamesForQuestion(
  row: {
    topic?: string | null
    categoryIds?: readonly string[] | null
    categoryUnassigned?: boolean
  },
  categories: readonly ExamCategory[] = DEFAULT_EXAM_CATEGORIES,
): string[] {
  const ids = (row.categoryIds ?? []).filter((id) => id.trim())
  if (row.categoryUnassigned && ids.length === 0) return []
  const explicit = ids
    .map((id) => findCategory([...categories], id)?.name)
    .filter((name): name is string => Boolean(name))
  if (explicit.length > 0) return explicit
  const details = categoryDetailsForQuestion(row)
  if (!details) return []
  return [details.skill ?? details.domain]
}

/** The first category, for sorting, grouping and filtering one column. */
export function categoryLabelForQuestion(
  row: {
    topic?: string | null
    categoryIds?: readonly string[] | null
    categoryUnassigned?: boolean
  },
  categories: readonly ExamCategory[] = DEFAULT_EXAM_CATEGORIES,
): string {
  return categoryNamesForQuestion(row, categories)[0] ?? ""
}

/** Display label for any centralized category id (domain, skill, or leaf). */
export function labelForContentId(contentId: string): string {
  return getCategoryPathLabel(DEFAULT_EXAM_CATEGORIES, contentId) || contentId
}

/** Skill-only label when nested under its domain in Recommended / Blueprint. */
export function skillLabelForContentId(contentId: string): string {
  const category = findCategory(DEFAULT_EXAM_CATEGORIES, contentId)
  if (!category) return contentId
  if (category.parentId === null) return category.name
  return category.name
}

/** True when the id exists in the centralized exam category library. */
export function isCentralizedCategoryId(categoryId: string): boolean {
  return findCategory(DEFAULT_EXAM_CATEGORIES, categoryId) != null
}

/**
 * Strict match for coverage matrix cells — a domain-tagged question counts
 * toward its domain row only, not every sibling leaf under that domain.
 */
export function questionCountsForCategoryRow(
  row: { topic?: string | null; categoryIds?: string[] },
  categoryId: string,
): boolean {
  // Every category the question carries, not just the first: a question tagged
  // in two places belongs to both places.
  const tagged = row.categoryIds?.filter((id) => id.trim()) ?? []
  const questionCategories =
    tagged.length > 0
      ? tagged
      : [leafIdForTopic(row.topic) ?? contentIdForTopic(row.topic)].filter((id): id is string => Boolean(id))
  return questionCategories.some(
    (questionCategory) =>
      questionCategory === categoryId ||
      getCategoryAncestors(DEFAULT_EXAM_CATEGORIES, questionCategory).includes(categoryId),
  )
}

/**
 * Whether a bank row can fill a gap in Add Questions / Recommended.
 * Domain-tagged items may fill an open leaf gap under the same domain until
 * questions carry finer category tags.
 */
export function questionMatchesCategoryId(
  row: { topic?: string | null; categoryIds?: string[] },
  categoryId: string,
): boolean {
  if (questionCountsForCategoryRow(row, categoryId)) return true
  const questionCategory =
    row.categoryIds?.[0] ?? leafIdForTopic(row.topic) ?? contentIdForTopic(row.topic)
  if (!questionCategory) return false
  const gapRoot = getRootCategoryId(DEFAULT_EXAM_CATEGORIES, categoryId)
  const questionRoot =
    getRootCategoryId(DEFAULT_EXAM_CATEGORIES, questionCategory) ??
    questionCategory
  return gapRoot != null && questionRoot === gapRoot
}

export function leafIdForTopic(topic: string | null | undefined): string | null {
  if (topic == null) return null
  const trimmed = topic.trim()
  if (!trimmed) return null
  return TOPIC_TO_LEAF[trimmed] ?? null
}

export function taskIdForBloom(bloom: string | null | undefined): string | null {
  if (bloom == null) return null
  const trimmed = bloom.trim()
  if (!trimmed) return null
  return BLOOM_TO_TASK[trimmed] ?? null
}

/** Bank facet topic label for a content domain (Add Questions filter). */
export function topicLabelForContent(contentId: string): string | null {
  const entry = Object.entries(TOPIC_TO_CONTENT).find(([, id]) => id === contentId)
  return entry?.[0] ?? null
}

export function bloomLabelForTask(taskId: string): string | null {
  const labels: Record<string, string> = {
    remembering: "Remember", understanding: "Understand", applying: "Apply",
    analyzing: "Analyze", evaluating: "Evaluate", creating: "Create",
  }
  return labels[taskId] ?? null
}
